import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import styled from 'styled-components'

const ControlsContainer = styled.div`
  padding: 12px 0 8px 0;
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SliderLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SliderValue = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 400;
`

const Slider = styled.input`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    transition: all 0.15s ease;
    border: 2px solid ${({ theme }) => theme.colors.background.primary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    &:hover {
      transform: scale(1.2);
    }
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.background.primary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: all 0.15s ease;

    &:hover {
      transform: scale(1.2);
    }
  }
`

const PresetButtons = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const PresetButton = styled.button<{ $isActive?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.colors.primary : theme.colors.border.primary};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primaryLight : theme.colors.background.primary};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const SectionDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 4px 0;
`

export interface SunlightLayerControlsProps {
  selectedLayerId: string
  currentDate?: Date
  onDateChange: (date: Date) => void
  /** Key to force re-render when date changes */
  dateKey?: string
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// Convert day of year (1-365) to Date
const dayOfYearToDate = (
  dayOfYear: number,
  year: number = new Date().getFullYear()
): Date => {
  const date = new Date(year, 0, 1)
  date.setDate(dayOfYear)
  return date
}

// Convert Date to day of year (1-365)
const dateToDayOfYear = (date: Date): number => {
  const start = new Date(date.getFullYear(), 0, 0)
  const diff = date.getTime() - start.getTime()
  const oneDay = 1000 * 60 * 60 * 24
  return Math.floor(diff / oneDay)
}

// Get month name and day from day of year
const getDayOfYearLabel = (
  dayOfYear: number,
  year: number = new Date().getFullYear()
): string => {
  const date = dayOfYearToDate(dayOfYear, year)
  const month = MONTH_NAMES[date.getMonth()]
  const day = date.getDate()
  return `${month} ${day}`
}

// Get time label in HH:MM format
const getTimeLabel = (hours: number): string => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

export const SunlightLayerControls: React.FC<SunlightLayerControlsProps> = ({
  selectedLayerId,
  currentDate = new Date(),
  onDateChange,
  dateKey,
}) => {
  const isShadowMode = selectedLayerId === 'sunlight-shadow-simulation'
  const isSunlightHoursMode = selectedLayerId === 'sunlight-hours'
  const year = currentDate.getFullYear()

  // State for sliders
  const [dayOfYear, setDayOfYear] = useState(() => dateToDayOfYear(currentDate))
  const [timeOfDay, setTimeOfDay] = useState(() => {
    const hours = currentDate.getHours()
    const minutes = currentDate.getMinutes()
    return hours + minutes / 60
  })

  // Update local state when currentDate or dateKey changes
  useEffect(() => {
    const newDayOfYear = dateToDayOfYear(currentDate)
    const hours = currentDate.getHours()
    const minutes = currentDate.getMinutes()
    const newTimeOfDay = hours + minutes / 60

    setDayOfYear(newDayOfYear)
    setTimeOfDay(newTimeOfDay)
  }, [currentDate, dateKey])

  // Handle day of year change
  const handleDayChange = useCallback(
    (value: number) => {
      setDayOfYear(value)
      const newDate = dayOfYearToDate(value, year)
      newDate.setHours(Math.floor(timeOfDay))
      newDate.setMinutes(Math.round((timeOfDay % 1) * 60))
      // Call immediately for real-time updates - no debouncing needed
      onDateChange(newDate)
    },
    [year, timeOfDay, onDateChange]
  )

  // Handle time of day change (shadow mode only)
  const handleTimeChange = useCallback(
    (value: number) => {
      setTimeOfDay(value)
      const newDate = dayOfYearToDate(dayOfYear, year)
      newDate.setHours(Math.floor(value))
      newDate.setMinutes(Math.round((value % 1) * 60))
      // Call immediately for real-time updates - no debouncing needed
      onDateChange(newDate)
    },
    [dayOfYear, year, onDateChange]
  )

  // Preset buttons - recreate dates each time to get current "now"
  const handlePresetClick = (
    presetType: 'now' | 'summer' | 'winter' | 'spring'
  ) => {
    const now = new Date()
    const currentYear = now.getFullYear()

    let date: Date
    switch (presetType) {
      case 'now':
        // For sunlight hours mode, use today at noon (time doesn't matter)
        // For shadow mode, use current time
        date = isSunlightHoursMode
          ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0)
          : now
        break
      case 'summer':
        date = new Date(currentYear, 5, 21, 12, 0) // June 21, noon
        break
      case 'winter':
        date = new Date(currentYear, 11, 21, 12, 0) // December 21, noon
        break
      case 'spring':
        date = new Date(currentYear, 2, 20, 12, 0) // March 20, noon
        break
    }

    onDateChange(date)
  }

  return (
    <ControlsContainer>
      <SectionDivider />

      {/* Day of Year Slider */}
      <SliderGroup>
        <SliderLabel>
          <span>Date</span>
          <SliderValue>{getDayOfYearLabel(dayOfYear, year)}</SliderValue>
        </SliderLabel>
        <Slider
          type="range"
          min="1"
          max="365"
          value={dayOfYear}
          onChange={(e) => handleDayChange(parseInt(e.target.value))}
        />
      </SliderGroup>

      {/* Time of Day Slider (Shadow Mode Only - not needed for Sunlight Hours) */}
      {isShadowMode && (
        <SliderGroup>
          <SliderLabel>
            <span>Time</span>
            <SliderValue>{getTimeLabel(timeOfDay)}</SliderValue>
          </SliderLabel>
          <Slider
            type="range"
            min="0"
            max="24"
            step="0.25"
            value={timeOfDay}
            onChange={(e) => handleTimeChange(parseFloat(e.target.value))}
          />
        </SliderGroup>
      )}

      {/* Info message for Sunlight Hours mode */}
      {isSunlightHoursMode && (
        <SliderLabel
          style={{ fontSize: '11px', fontStyle: 'italic', opacity: 0.7 }}
        >
          Showing daily sun exposure from sunrise to sunset
        </SliderLabel>
      )}

      {/* Preset Buttons */}
      <PresetButtons>
        <PresetButton onClick={() => handlePresetClick('now')}>
          {isShadowMode ? 'Now' : 'Today'}
        </PresetButton>
        <PresetButton onClick={() => handlePresetClick('summer')}>
          Summer
        </PresetButton>
        <PresetButton onClick={() => handlePresetClick('winter')}>
          Winter
        </PresetButton>
        <PresetButton onClick={() => handlePresetClick('spring')}>
          Spring
        </PresetButton>
      </PresetButtons>
    </ControlsContainer>
  )
}
